import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} CreateSchoolSessionData
 * @property {"draft"|"active"} status - required
 * @property {string} academic_year - required (e.g. 2025-26)
 * @property {string} starts_at - required (YYYY-MM-DD)
 * @property {string} ends_at - required (YYYY-MM-DD)
 */

export const useCreateSchoolSession = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { axiosInstance } = useAxios();

  /**
   * Create / Store School Session
   * @param {object} params
   * @param {CreateSchoolSessionData} params.sessionData
   * @param {()=>void} params.onSuccess
   * @param {(message:string)=>void} params.onError
   */
  const createSchoolSession = async ({ sessionData, onSuccess, onError }) => {
    try {
      setLoading(true);
      setError("");

      const payload = {
        status: sessionData.status,
        academic_year: sessionData.academic_year,
        starts_at: sessionData.starts_at,
        ends_at: sessionData.ends_at,
      };

      await axiosInstance.post(
        `${TENANT_DOMAIN}/school/admin/settings/session/store`,
        payload,
      );

      onSuccess();
    } catch (err) {
      let firstErrorMessage = ERROR_MESSAGES.INTERNAL_SERVER;

      // Custom API error message
      if (err?.response?.data?.message) {
        firstErrorMessage = err.response.data.message;
      }

      // Tenant mismatch or known backend error
      if (err?.response?.data?.error_code) {
        firstErrorMessage = err.response.data.message;
      }

      // Validation errors (if backend sends `errors`)
      if (err?.response?.data?.errors) {
        firstErrorMessage = Object.values(err.response.data.errors)?.[0]?.[0];
      }

      if (err?.response?.status === 500) {
        firstErrorMessage = ERROR_MESSAGES.INTERNAL_SERVER;
      }

      setError(firstErrorMessage || ERROR_MESSAGES.UNKNOWN);
      onError(firstErrorMessage || ERROR_MESSAGES.UNKNOWN);
    } finally {
      setLoading(false);
    }
  };

  return {
    createSchoolSession,
    loading,
    error,
  };
};
